import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { Skeleton } from '@/components/ui/skeleton'
import { Stepper } from '@/components/ui/stepper'
import { ListingTypeSelect } from '@/features/luxe-submissions/components/listing-type-select'
import { SubmissionFooter } from '@/features/luxe-submissions/components/submission-footer'
import { SubmissionFormHeader } from '@/features/luxe-submissions/components/submission-form-header'
import { SubmissionSuccess } from '@/features/luxe-submissions/components/submission-success'
import { AirbnbListingSchema } from '@/features/luxe-submissions/form-schemas'
import {
	httpGetListingData,
	type ResolveAirbnbListingData,
	type SubmitListingResponse
} from '@/features/luxe-submissions/http/luxe-submissions.http'
import { useSubmitAirbnbListing } from '@/features/luxe-submissions/queries/use-submit-airbnb-listing'
import { debounce } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'

const STRING_FIELDS = ['listingName', 'listingId', 'hostEmail', 'geo'] as const
const NUMBER_FIELDS = ['bedrooms', 'beds', 'bathrooms', 'guestCount'] as const
const LISTING_FIELDS = [...STRING_FIELDS, ...NUMBER_FIELDS] as const
const roundNumberField = (fieldValue: number | null) =>
	fieldValue ? Math.floor(fieldValue) : null

function LuxeSubmissionsSubmissionListing() {
	const { t } = useTranslation()
	const [showFields, setShowFields] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [submittedData, setSubmittedData] =
		useState<SubmitListingResponse | null>(null)
	const [isInvalidLink, setIsInvalidLink] = useState(false)
	const [listingMetadata, setListingMetadata] =
		useState<ResolveAirbnbListingData | null>(null)
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		trigger,
		formState: { errors }
	} = useForm({
		resolver: zodResolver(AirbnbListingSchema),
		defaultValues: {
			listingName: '',
			hostEmail: '',
			geo: '',
			listingUrl: '',
			listingId: '',
			listingInventory: '' as const,
			bedrooms: 0,
			beds: 0,
			bathrooms: 0,
			guestCount: 0
		}
	})

	const [listingUrl, listingInventory] = watch([
		'listingUrl',
		'listingInventory'
	])

	const clearListingFields = useCallback(() => {
		for (const field of STRING_FIELDS) {
			setValue(field, '')
		}
		for (const field of NUMBER_FIELDS) {
			setValue(field, 0)
		}
		setListingMetadata(null)
	}, [setValue])

	const fetchListingData = useMemo(
		() =>
			debounce(async (url: string) => {
				const isValid = await trigger('listingUrl')
				if (!isValid) {
					clearListingFields()
					setShowFields(false)
					setIsLoading(false)
					setIsInvalidLink(false)
					return
				}

				setIsInvalidLink(false)
				setIsLoading(true)
				try {
					const response = await httpGetListingData(url)
					const { data: listing } = response
					const fieldMap = {
						listingId: listing.airbnbListingId,
						listingName: listing.listingName,
						geo: listing.city,
						bedrooms: roundNumberField(listing.bedrooms),
						beds: roundNumberField(listing.beds),
						bathrooms: roundNumberField(listing.bathrooms),
						guestCount: roundNumberField(listing.personCapacity)
					} as const

					for (const [field, value] of Object.entries(fieldMap)) {
						if (value != null) {
							setValue(
								field as (typeof LISTING_FIELDS)[number],
								value
							)
						}
					}
					setListingMetadata(listing)
					setShowFields(true)
				} catch {
					clearListingFields()
					setShowFields(false)
					setIsInvalidLink(true)
				} finally {
					setIsLoading(false)
				}
			}, 500),
		[setValue, trigger, clearListingFields]
	)

	useEffect(() => {
		if (!(listingUrl && listingInventory)) {
			setShowFields(false)
			if (!listingUrl) {
				setIsInvalidLink(false)
			}
			return
		}
		fetchListingData(listingUrl)
	}, [listingUrl, listingInventory, fetchListingData])

	const { mutate: submitListing, isPending } = useSubmitAirbnbListing({
		onSuccess: (data) => {
			setSubmittedData(data)
		}
	})

	const onSubmit = handleSubmit((data) => {
		if (!listingMetadata) {
			return
		}
		submitListing({
			airbnbUrl: data.listingUrl,
			listingInventory: data.listingInventory,
			hostEmail: data.hostEmail,
			listingName: data.listingName,
			geo: data.geo,
			bedrooms: data.bedrooms,
			bathrooms: data.bathrooms,
			beds: data.beds,
			guestCount: data.guestCount,
			propertyType: listingMetadata.propertyType,
			amenities: listingMetadata.amenities
				? listingMetadata.amenities
				: undefined,
			photos: listingMetadata.photos ? listingMetadata.photos : undefined,
			description: listingMetadata.description,
			hostName: listingMetadata.hostName,
			country: listingMetadata.country,
			hostIsSuperhost: listingMetadata.hostIsSuperhost,
			hostProfilePictureUrl: listingMetadata.hostProfilePictureUrl,
			lat: listingMetadata.lat,
			lng: listingMetadata.lng,
			roomType: listingMetadata.roomType,
			hostExternalId: listingMetadata.hostExternalId,
			walkthroughContactEmail: data.walkthroughContactEmail
				? data.walkthroughContactEmail
				: undefined
		})
	})

	if (submittedData) {
		return <SubmissionSuccess submissionDetails={submittedData} />
	}

	return (
		<div className='flex flex-1 flex-col overflow-hidden bg-neutral-white'>
			<SubmissionFormHeader />

			<div className='flex flex-1 flex-col overflow-auto'>
				<form
					className='m:m-6 flex flex-col gap-6 m:rounded-3xl m:border m:border-neutral-300 m:p-6 px-6 py-4'
					id='submission-form'
					onSubmit={onSubmit}
				>
					<h2 className='font-bold text-lg text-neutral-900'>
						{t('luxe_submissions:submission_details')}
					</h2>

					<div className='flex m:grid m:grid-cols-2 flex-col gap-6'>
						<Controller
							control={control}
							name='listingUrl'
							render={({ field }) => (
								<InputText
									{...field}
									errorMessage={
										isInvalidLink
											? t(
													'luxe_submissions:airbnb_invalid_link'
												)
											: t(
													errors.listingUrl
														?.message as TKey
												)
									}
									helpText={t(
										'luxe_submissions:listing_url_hint'
									)}
									label={t('luxe_submissions:listing_url')}
									placeholder={t(
										'luxe_submissions:listing_url_placeholder'
									)}
									type='url'
								/>
							)}
						/>

						<Controller
							control={control}
							name='listingInventory'
							render={({ field }) => {
								return (
									<ListingTypeSelect
										errorMessage={
											errors.listingInventory
												?.message as TKey
										}
										name={field.name}
										onChange={(val) =>
											field.onChange(val ?? '')
										}
										value={field.value ?? ''}
									/>
								)
							}}
						/>

						{isLoading && (
							<div className='col-span-2 flex m:grid m:grid-cols-2 flex-col gap-4'>
								{LISTING_FIELDS.map((field) => (
									<div
										className='flex flex-col gap-2'
										key={field}
									>
										<Skeleton height={16} width={100} />
										<Skeleton height={48} />
									</div>
								))}
							</div>
						)}

						{showFields && !isLoading && (
							<>
								<Controller
									control={control}
									name='listingName'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.listingName
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:listing_name'
											)}
											placeholder={t(
												'luxe_submissions:listing_name_placeholder'
											)}
											type='text'
										/>
									)}
								/>

								<Controller
									control={control}
									name='geo'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.geo?.message as TKey
											)}
											label={t('luxe_submissions:geo')}
											placeholder={t(
												'luxe_submissions:geo_placeholder'
											)}
											type='text'
										/>
									)}
								/>

								<Controller
									control={control}
									name='listingId'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.listingId
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:listing_id'
											)}
											placeholder={t(
												'luxe_submissions:listing_id_placeholder'
											)}
											type='text'
										/>
									)}
								/>

								<Controller
									control={control}
									name='hostEmail'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.hostEmail
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:host_email'
											)}
											placeholder={t(
												'luxe_submissions:host_email_placeholder'
											)}
											type='email'
										/>
									)}
								/>
								<Controller
									control={control}
									name='walkthroughContactEmail'
									render={({ field }) => (
										<InputText
											{...field}
											errorMessage={t(
												errors.walkthroughContactEmail
													?.message as TKey
											)}
											label={t(
												'luxe_submissions:walkthrough_contact_email'
											)}
											placeholder={t(
												'luxe_submissions:contact_email_placeholder'
											)}
											type='email'
										/>
									)}
								/>
								<div className='col-start-1 flex flex-col gap-2'>
									<p className='font-medium text-base text-grey-750'>
										{t('luxe_submissions:property_details')}
									</p>
									<div className='m:flex grid grid-cols-2 gap-x-15 gap-y-4'>
										<Controller
											control={control}
											name='bedrooms'
											render={({ field }) => (
												<Stepper
													errorMessage={t(
														errors.bedrooms
															?.message as TKey
													)}
													label={t(
														'luxe_submissions:bedrooms_count'
													)}
													min={0}
													name={field.name}
													onChange={field.onChange}
													value={field.value}
												/>
											)}
										/>

										<Controller
											control={control}
											name='beds'
											render={({ field }) => (
												<Stepper
													errorMessage={t(
														errors.beds
															?.message as TKey
													)}
													label={t(
														'luxe_submissions:bed_count'
													)}
													min={0}
													name={field.name}
													onChange={field.onChange}
													value={field.value}
												/>
											)}
										/>

										<Controller
											control={control}
											name='bathrooms'
											render={({ field }) => (
												<Stepper
													errorMessage={t(
														errors.bathrooms
															?.message as TKey
													)}
													label={t(
														'luxe_submissions:bathrooms_count'
													)}
													min={0}
													name={field.name}
													onChange={field.onChange}
													value={field.value}
												/>
											)}
										/>

										<Controller
											control={control}
											name='guestCount'
											render={({ field }) => (
												<Stepper
													errorMessage={t(
														errors.guestCount
															?.message as TKey
													)}
													label={t(
														'luxe_submissions:guest_count'
													)}
													min={0}
													name={field.name}
													onChange={field.onChange}
													value={field.value}
												/>
											)}
										/>
									</div>
								</div>
							</>
						)}
					</div>
				</form>
			</div>

			<SubmissionFooter
				disabled={!showFields || isLoading || isPending}
				form='submission-form'
				isLoading={isPending}
				label={t('luxe_submissions:submit_listing')}
			/>
		</div>
	)
}

export { LuxeSubmissionsSubmissionListing }
